import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Modal,
  Select,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { DrawerForm } from "../../Common/styles/drawer";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { toggleResponseState } from "../../../Redux/reducers/responseSlice";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../Redux/hooks";

const schema = yup.object().shape({
  eligibilityStatus: yup.string().required("Eligibility Status is Required"),
  participantGroupId: yup
    .string()
    .when("eligibilityStatus", ([eligibilityStatus], schema) => {
      return eligibilityStatus === "included"
        ? schema.required("Cohort is Required")
        : schema.optional();
    }),
});

const statusList = [
  { label: "Included", value: "included" },
  { label: "Excluded", value: "excluded" },
  { label: "Decide Later", value: "decide_later" },
];

const FinishReviewModal = ({
  showModal,
  closeModal,
  changeStatus,
}: any) => {
  const [participantGroups, setParticipantGroups] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [filteredStatusList] = useState(
    changeStatus
      ? statusList.filter((item) => item.value !== "decide_later")
      : statusList
  );

  const { id: studyId, participantId } = useParams();
  const dispatch = useDispatch();
  const screeningAttemptId = useAppSelector(
    (state) => state.response.screeningAttemptId
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(
          `/study/${studyId}/participant-groups?page=1&size=100`
        );
        const formattedData = res.data?.data?.data?.map((group: any) => ({
          id: group?.id,
          name: group?.name,
        }));
        setParticipantGroups(formattedData);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    fetchData();
  }, [setLoading, studyId]);

  const handleSubmit = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        ...values,
        participantId,
      };

      if (body.participantGroupId === "") {
        delete body.participantGroupId;
      }

      if (!changeStatus) {
        const params = new URLSearchParams({
          participantId: participantId || "",
        });
        await http.post(
          `/study/${studyId}/admin-screening-responses/attempt/${screeningAttemptId}/finish?${params}`
        );
      }

      const res: AxiosResponse = await http.post(
        `/study/${studyId}/participant-groups/screen`,
        body
      );
      toastMessage("success", res?.data?.message);
      dispatch(toggleResponseState());
      closeModal();
    } catch (error) {
      errorToastMessage(error as Error);
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={changeStatus ? "Change Status" : "Finish Review"}
          onCloseClick={closeModal}
        />
        {!loading ? (
          <Formik
            initialValues={{
              participantGroupId: "",
              eligibilityStatus: "",
            }}
            validationSchema={schema}
            onSubmit={(values) => {
              handleSubmit(values);
            }}
          >
            {({ setFieldValue, values, errors, touched }) => (
              <Form style={DrawerForm}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="status">
                    Eligibility Status <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    value={values?.eligibilityStatus}
                    onChange={(e) => {
                      if (e.target.value !== "included") {
                        setFieldValue("participantGroupId", "");
                      }
                      setFieldValue("eligibilityStatus", e.target.value);
                    }}
                  >
                    {filteredStatusList?.map((group) => (
                      <MenuItem key={group.value} value={group.value}>
                        {group.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {touched?.eligibilityStatus && errors?.eligibilityStatus
                      ? (errors?.eligibilityStatus as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                {values?.eligibilityStatus === "included" && (
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="group">
                      Cohort <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Select
                      value={values?.participantGroupId}
                      onChange={(e) =>
                        setFieldValue("participantGroupId", e.target.value)
                      }
                    >
                      {participantGroups?.map((group: any) => (
                        <MenuItem key={group?.id} value={group?.id}>
                          {group?.name || "-"}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error>
                      {touched?.participantGroupId && errors?.participantGroupId
                        ? (errors?.participantGroupId as string)
                        : " "}
                    </FormHelperText>
                  </FormControl>
                )}
                <Box sx={{ mt: "auto" }}>
                  <Divider />
                  <Box sx={ModalDrawerButtonStyles}>
                    {!submitLoader ? (
                      <>
                        <Button onClick={closeModal} variant="outlined">
                          Cancel
                        </Button>
                        <Button type="submit" variant="contained">
                          Finish
                        </Button>
                      </>
                    ) : (
                      <CircularProgress size={25} />
                    )}
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default FinishReviewModal;
